import { useMemo } from 'react';

import { getPublicOtherFilters } from '~/components/Filters/utils/getPublicOtherFilters';
import {
  type CustomFieldFilterType,
  type FilterType,
  IPTC_FIELDS,
  sortFiltersByOrder,
} from '~/components/FiltersMenu/types';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { usePublicCustomFieldsContext } from '~/providers/PublicCustomFieldsProvider';
import { type SelectedFilter } from '~/store/filters/types';
import { getSelectCustomFields } from '~/swr-hooks/customFields/utils';
import { canSeeAssetVersions, canViewAssetDiscussions } from '~/utils/permissions/assetPermissions';

export const usePublicDefaultFastFilterBarOptions = () => {
  const { customFields } = usePublicCustomFieldsContext();
  const { permissions } = usePublicPermissions();
  const selectCustomFields = useMemo(() => (customFields ? getSelectCustomFields(customFields) : []), [customFields]);

  const availableOtherFilters = useMemo(
    () =>
      getPublicOtherFilters({
        canViewAssetVersions: canSeeAssetVersions(permissions),
        canViewDiscussions: canViewAssetDiscussions(permissions),
      }),
    [permissions],
  );

  const { filters, moreFilters, moreCustomFields, disabledFilters } = useMemo(() => {
    const filters: SelectedFilter[] = [
      {
        type: 'type',
      },
    ];
    const disabledFilters: FilterType[] = [];
    const customFieldsInAdvanced: CustomFieldFilterType[] = [];

    if (selectCustomFields.length) {
      customFieldsInAdvanced.push(...selectCustomFields);
    }

    const advancedFilters: FilterType[] = [
      'assetFileKind',
      'uploader',
      'color',
      'source',
      'extension',
      'orientation',
      'dateCreated',
      'dateModified',
      'dateUploaded',
    ];
    if (availableOtherFilters.length > 0) {
      advancedFilters.push('separator2');
      advancedFilters.push('other');
    }

    advancedFilters.push(...IPTC_FIELDS);
    disabledFilters.push(...IPTC_FIELDS);

    if (customFieldsInAdvanced.length) {
      advancedFilters.push('customField');
    }

    return {
      filters,
      moreFilters: advancedFilters.sort(sortFiltersByOrder),
      moreCustomFields: customFieldsInAdvanced,
      disabledFilters,
    };
  }, [availableOtherFilters.length, selectCustomFields]);

  return {
    filters,
    moreFilters,
    moreCustomFields,
    disabledFilters,
  };
};
